"use client";

/* Generated by Copilot- Start */
import { ComponentNames } from "lib/constant/component-constant";
import React from "react";
import { ComponentClientWrapper } from "./component-client-wrapper";
import { ComponentMap } from "./component-map";
import WncMainCanvasErrorDisplay from "./wnc-main-canvas-error-display-client";
import { useUserRecognition } from "hooks/useRecognizedUserData";
import FaqPageJsonLd from "@/FaqPageJsonLd";
import { withFaqMicrodataDisabled } from "src/utils/faqPageJsonLd";

/**
 * Number of non-spacer components to render above the fold
 */
const ABOVE_THE_FOLD_COUNT = 2;

/**
 * Component types that should not be counted toward above-the-fold limit.
 * BREADCRUMBSV2 carries no image (text nav only) so it can never be the LCP;
 * counting it would consume an above-the-fold slot and push the real hero
 * (e.g. DtvMarquee) out of the eager-load window -> lazy LCP. Kept in parity
 * with the Pages Router canvas (src/components/WnCMainCanvas/WnCMainCanvas.tsx).
 */
const NON_COUNTABLE_TYPES = ["VERTICALSPACER", "FLOATINGWIDGETV2", "BREADCRUMBSV2"] as const;

/**
 * Props interface for WnCMainCanvasServer
 */
export interface WnCMainCanvasServerProps {
  /** Array of component configurations to render */
  components?: any[];
  /** CMS type (e.g., 'sb' for Storyblok) */
  cms_type?: string;
  /** Environment identifier (e.g., 'prod', 'dev') */
  env?: string;
  /** URL query parameters */
  reqQueryParams?: Record<string, string>;
  /** Initial recognized user data from server-side */
  initialRecognizedUserData?: any;
  /** Component options including auth and feature flags */
  options?: {
    isAuthenticatedUser?: boolean;
    enableConfigureCart?: boolean;
    [key: string]: any;
  };
}

/**
 * Interface for component configuration
 */
interface ComponentConfig {
  type: string;
  props?: any;
}

/**
 * Resolves the actual component type to render, handling special cases
 * like OVERWRITER and conditional routing based on query parameters.
 */
function resolveComponentType(
  component: ComponentConfig,
  reqQueryParams?: Record<string, string>
): string | null {
  const { type, props } = component;

  // Handle OVERWRITER component (component wrapper/override pattern)
  if (type === "OVERWRITER") {
    return props?.component || null;
  }

  // Handle CHANNEL_COMPARISON_TOOL routing based on 'channels' query param
  if (type === "CHANNEL_COMPARISON_TOOL" && reqQueryParams?.channels === "internet") {
    return ComponentNames.CHANNEL_COMPARISON_TOOL_V2;
  }

  // Handle CHANNEL_LINEUP_FFP routing based on 'myFreeDtv' query param
  if (type === "CHANNEL_LINEUP_FFP" && reqQueryParams?.myFreeDtv === "true") {
    return ComponentNames.CHANNEL_LINEUP_FFP;
  }

  return type;
}

/**
 * Extracts component props, handling OVERWRITER special case
 */
function extractComponentProps(component: ComponentConfig): any {
  if (component.type === "OVERWRITER") {
    const objProps = component.props?.objProps;
    return Array.isArray(objProps) ? objProps[0] : objProps;
  }
  return component.props;
}

/**
 * Builds enhanced component props with auth, feature flags, and environment-specific overrides
 */
function buildEnhancedProps(
  componentProps: any,
  component: ComponentConfig,
  isAboveTheFold: boolean,
  options: {
    clientSideIsUserAuthenticated?: boolean;
    clientSideRecognizedUserData?: any;
    enableConfigureCart?: boolean;
    env?: string;
    reqQueryParams?: Record<string, string>;
  }
): any {
  const {
    clientSideIsUserAuthenticated,
    clientSideRecognizedUserData,
    enableConfigureCart,
    env,
    reqQueryParams
  } = options;

  const enhancedOptions = {
    ...componentProps.options,
    isAuthenticatedUser: clientSideIsUserAuthenticated,
    recognizedUserData: clientSideRecognizedUserData,
    isQuizv2: component.type === "QUIZV2"
  };

  // Apply package orientation override in non-prod environments
  const pkgOrientationOverride =
    env !== "prod" && reqQueryParams?.["pkg-orientation"] ?
      { orientation: reqQueryParams["pkg-orientation"] } :
      {};

  return {
    aboveTheFold: isAboveTheFold,
    lazyLoadImages: !isAboveTheFold,
    ...componentProps,
    options: enhancedOptions,
    enableConfigureCart,
    ...pkgOrientationOverride
  };
}

/**
 * Checks if a component type should be counted toward above-the-fold limit
 */
function shouldCountComponent(type: string): boolean {
  return !NON_COUNTABLE_TYPES.includes(type as any);
}

/**
 * Main canvas component that renders a list of dynamic components with
 * above-the-fold optimization and user authentication context.
 *
 * TODO: Move useUserRecognition hook to video-lab and use it in respective components,
 * then remove "use client" directive from this file. Also remove suspense fallback
 * for below-the-fold components once useMediaQuery is removed from client components,
 * ensuring all components are server-rendered without hydration issues.
 */
export function WnCMainCanvasServer(props: WnCMainCanvasServerProps) {
  const {
    components = [],
    cms_type,
    env,
    reqQueryParams,
    initialRecognizedUserData,
    options = {}
  } = props;

  // Track count of non-spacer components for above-the-fold determination
  let nonSpacerCount = -1;

  // Get client-side user authentication and recognition data
  const { clientSideIsUserAuthenticated, clientSideRecognizedUserData } =
    useUserRecognition({
      initialRecognizedUserData,
      isUserAuthenticated: options.isAuthenticatedUser
    });

  return (
    <>
      {components.map((component: any, index: number) => {
        try {
          // Ensure component has a type
          if (!component?.type) {
            return null;
          }

          // Suppress the standalone section header that immediately precedes a parallax Show
          // Carousel: the parallax variant renders its own title, so a title-only PAGESECTIONHEADER
          // above it is a duplicate. Both blocks may arrive OVERWRITER-wrapped, so resolve the
          // effective type via resolveComponentType (as the render path does). Kept in lockstep with
          // the Pages canvas guard (WnCMainCanvas.tsx).
          if (resolveComponentType(component, reqQueryParams) === "PAGESECTIONHEADER") {
            // Skip layout-only spacers so an interleaved VERTICALSPACER doesn't defeat the guard.
            let lookahead = index + 1;
            while (
              components[lookahead] &&
              resolveComponentType(components[lookahead], reqQueryParams) === "VERTICALSPACER"
            ) {
              lookahead += 1;
            }
            const nextBlock: any = components[lookahead];
            // Resolve the variant with the same objProps-tolerant chain the Pages canvas uses,
            // so suppression fires identically in both routers regardless of nesting.
            const nextVariant =
              nextBlock?.props?.variant ||
              nextBlock?.props?.objProps?.variant ||
              nextBlock?.props?.objProps?.[0]?.variant;
            // The parallax child renders its heading only when the carousel carries one, so only
            // drop the standalone header when the carousel has a title to replace it with. Parallax
            // authors its title in the top-level `header` (the source the parallax render falls back
            // to), so key off that alone — kept in lockstep with the Pages canvas.
            const nextHeaderSrc = nextBlock ? extractComponentProps(nextBlock) : undefined;
            const nextHeader = nextHeaderSrc?.header;
            const nextHeaderNode = Array.isArray(nextHeader) ? nextHeader?.[0] : nextHeader;
            const nextHasOwnTitle =
              typeof nextHeaderNode === "string" ?
                !!nextHeaderNode :
                !!(nextHeaderNode?.content || nextHeaderNode?.title);
            // The carousel only replaces the TITLE. Never drop a header that also carries an eyebrow,
            // body copy, legal disclosure, a CTA, an image, a sticker or icon text — the parallax
            // variant renders none (fields per DtvPageSectionHeaderProps).
            const headerProps = extractComponentProps(component);
            const headerIsTitleOnly = !(
              headerProps?.eyebrow?.content ||
              headerProps?.body?.content ||
              headerProps?.legal ||
              headerProps?.cta?.label ||
              headerProps?.cta?.imageUrl ||
              headerProps?.optionalCta?.label ||
              headerProps?.optionalCta?.imageUrl ||
              headerProps?.image?.imageUrl ||
              headerProps?.image?.tabletImgUrl ||
              headerProps?.image?.mobileImgUrl ||
              headerProps?.sticker ||
              headerProps?.iconText ||
              headerProps?.iconImageUrl
            );
            if (
              nextBlock &&
              resolveComponentType(nextBlock, reqQueryParams) === "SHOWCAROUSAL" &&
              nextVariant === "parallax" &&
              nextHasOwnTitle &&
              headerIsTitleOnly
            ) {
              return null;
            }
          }

          // Resolve the actual component type to render
          const resolvedType = resolveComponentType(component, reqQueryParams);
          if (!resolvedType) {
            if (process.env.NODE_ENV !== "production") {
              console.warn(`Unable to resolve component type for index ${index}`);
            }
            return null;
          }

          // Pages skips a block it can't render before counting it (`if (comp)`), so an
          // unmapped type (e.g. legacy BREADCRUMBS) never shifts the fold or content-visibility.
          if (!ComponentMap[resolvedType as keyof typeof ComponentMap]) {
            if (process.env.NODE_ENV !== "production") {
              console.warn(`Component of type "${resolvedType}" not found in ComponentMap.`);
            }
            return null;
          }

          // Increment counter for countable components
          if (shouldCountComponent(component.type)) {
            // eslint-disable-next-line no-plusplus
            nonSpacerCount++;
          }

          // Extract and enhance component props
          const baseProps = extractComponentProps(component);
          // Non-countable types (spacers, breadcrumbs) are never above-the-fold here.
          // The Pages Router canvas leaves the flag truthy for breadcrumbs but these
          // types render no LCP image, so the eager-loading behavior is equivalent.
          const isAboveTheFold =
            nonSpacerCount < ABOVE_THE_FOLD_COUNT &&
            shouldCountComponent(component.type);
          // WAT-12433: content-visibility follows the Pages Router position rule
          // (nonSpacerCount > aboveTheFoldCount), not !isAboveTheFold — that is always
          // true for a spacer, and the containment kept its margin, adding ~100px.
          const contentVisibilityEligible = nonSpacerCount > ABOVE_THE_FOLD_COUNT;

          const enhancedProps = buildEnhancedProps(
            baseProps || {},
            component,
            isAboveTheFold,
            {
              clientSideIsUserAuthenticated,
              clientSideRecognizedUserData,
              enableConfigureCart: options.enableConfigureCart,
              env,
              reqQueryParams
            }
          );

          // Render component wrapper. HIM-4783: an FAQ blok no longer stamps its own
          // itemscope/itemtype/itemprop — the page-level JSON-LD below is the single
          // FAQPage claim now. Kept in parity with the Pages Router canvas.
          return (
            <ComponentClientWrapper
              aboveTheFold={isAboveTheFold}
              cms_type={cms_type}
              componentKey={index}
              componentProps={withFaqMicrodataDisabled(resolvedType, enhancedProps)}
              contentVisibilityEligible={contentVisibilityEligible}
              key={index}
              type={resolvedType}
            />
          );
        } catch (error) {
          // Render error display component on failure
          return <WncMainCanvasErrorDisplay error={error} key={`error-${index}`} />;
        }
      })}
      {/* HIM-4783: the page's one FAQPage claim, built from every FAQ blok above. */}
      <FaqPageJsonLd components={components} />
    </>
  );
}

/* Generated by Copilot- End */
